import React, { useCallback, useRef, useState } from "react"
import EventResourcesView from "components/layout/EventResourcesView"
import { useParams } from "react-router-dom"
import usePermissions from "services/permissions"
import { useQueryClient } from "react-query"
import {
  EventCounts, GuestSummary, ModalName, Table as DioboxTable,
} from "sharedTypes"
import * as guestsApi from "api/guests"
import { updateGuest, updateTables } from "context/actions"
import EventPage, { EventPageHeader } from "components/layout/EventPage"
import TablesHeader from "pages/EventShow/Seating/TablesHeader"
import _ from "lodash"
import Link from "components/elements/Link"
import { Box, Text } from "@chakra-ui/react"
import { useGuests } from "queries/guests"
import useModal from "services/useModal"
import { naturalSortBy } from "utilities/arrays"
import GuestList from "components/GuestList/GuestList"
import Tables from "./Tables"
import ListHeader from "../Guests/ScrollView/GuestListHeader"
import Table from "./Table"
import GuestListItem from "./GuestListItem"
import NoTables from "./NoTables"
import { updateTablesFromGuest } from "../Guests/SelectedGuest/Show/Seating"

interface Props {
  eventId: string
  counts: EventCounts
  tables: DioboxTable[]
  maxTables: number|null
}

const Seating = ({
  eventId, counts, tables, maxTables,
}: Props) => {
  const { eventId: eventIdParam, tableId } = useParams<{ eventId: string, tableId: string }>()
  const queryClient = useQueryClient()
  const showModal = useModal()
  const [order, setOrder] = useState<{ field: string; order: "asc" | "desc" }>({ field: "name", order: "asc" })
  const [searchString, setSearchString] = useState("")
  const [guestsOrder, setGuestsOrder] = useState("first_name_asc")
  const overTablesLimit = maxTables && tables.length >= maxTables
  const { canEditSeating } = usePermissions()
  const scrollRef = useRef<HTMLDivElement>(null)

  const {
    guests,
    fetchNextPage,
    isLoading,
  } = useGuests(eventId, searchString, guestsOrder)

  const assignSeat = useCallback((guest: GuestSummary, table: DioboxTable, seatNumber: number) => {
    guestsApi.postGuestSeat(eventIdParam, guest, table, seatNumber)
      .then(({ data }) => {
        updateGuest(queryClient, data)
        updateTables(queryClient, eventIdParam, updateTablesFromGuest(data, tables))
      })
  }, [eventIdParam, queryClient, tables])

  if (!tables.length) {
    return (
      <NoTables
        canEditSeating={canEditSeating}
        onClick={() => showModal(ModalName.TableForm)}
      />
    )
  }

  let table: DioboxTable | undefined
  if (tableId) {
    table = tables.find(({ id }) => id.toString() === tableId)
  }

  const extendedTables = tables.map((t) => {
    const seatCount = t.seats.length
    const waitlistedGuestCount = t.waitlistedGuests.length
    const vacantSeatCount = t.seats.filter(({ guest, reserved }) => !guest && !reserved).length

    return {
      ...t,
      seatCount,
      vacantSeatCount,
      waitlistedGuestCount,
    }
  })

  const sortedTables = order.field === "name"
    ? naturalSortBy(extendedTables, "name")
    : _.orderBy(extendedTables, order.field, order.order)

  return (
    <Box h="full" overflow="hidden" position="relative">
      {table && (
      <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={10} bg="white" overflowY="scroll">
        <Table table={table} />
      </Box>
      )}
      <EventPage
        scrollableBody={false}
        header={(
          <EventPageHeader
            icon="seating"
            title="Seating"
            helpText={(
              <Text p={1}>
                After creating guests and tables, you can drag guests to seats.{" "}
                <Link isExternal href="https://home.d.io/tables-seating">Learn more about
                  seating.
                </Link>
              </Text>
              )}
            items={(
              <TablesHeader
                tableCount={counts.tables}
                seatCount={counts.seats}
                vacantSeatCount={counts.unassignedSeats}
                order={order}
                onChangeOrder={setOrder}
                canEditSeating={canEditSeating && !overTablesLimit}
              />
              )}
          />
          )}
        body={(
          <EventResourcesView
            showBorder={false}
            bgColor="gray.50"
            listHeader={(
              <ListHeader
                loading={isLoading}
                searchString={searchString}
                onSearch={(query) => setSearchString(query)}
                order={guestsOrder}
                onSetOrder={(value) => setGuestsOrder(value)}
              />
            )}
            listItems={(
              <GuestList guests={guests} filtered={!!searchString} scrollRef={scrollRef}>
                {(guest, plusOne) => (
                  <GuestListItem
                    guest={guest}
                    order={guestsOrder}
                    plusOne={plusOne}
                    canEditSeating={canEditSeating}
                    onDropOnSeat={assignSeat}
                  />
                )}
              </GuestList>
              )}
            selectedItem={<Tables tables={sortedTables} overTablesLimit={overTablesLimit} />}
            onScrollEnd={fetchNextPage}
            scrollViewRef={scrollRef}
            listWidth="md"
          />
          )}
      />
    </Box>
  )
}

export default Seating
